<template>
  <modal name="modal-fleet-brands" class="modal-inner" transition="pop-out" :width="820" :height="'auto'" :scrollable="true" :adaptive="true">
    <!-- Header -->
    <div class="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200 sticky top-0 z-10">
      <div class="flex items-center gap-3">
        <h3 class="text-base font-semibold text-gray-800">{{ $t('fleet_management.manage_brands_title') }}</h3>
        <span class="text-xs text-gray-400">{{ $t('fleet_management.manage_brands_subtitle') }}</span>
      </div>
      <div class="flex items-center gap-2">
        <button class="btn-secondary-outline text-xs px-3 py-1.5 flex items-center gap-1" @click="seedDefaults" :disabled="isSeeding">
          <BaseIcon v-if="isSeeding" icon="spinner" class="animate-spin" style="font-size:11px" />
          <BaseIcon v-else icon="database" style="font-size:11px" />
          {{ $t('fleet_management.import_defaults') }}
        </button>
        <button @click="hide" class="text-gray-400 hover:text-gray-600 transition-colors ml-1">
          <BaseIcon icon="times" />
        </button>
      </div>
    </div>

    <!-- Two-panel body -->
    <div class="flex" style="min-height: 500px; max-height: 600px;">

      <!-- LEFT: Brands list -->
      <div class="w-64 flex-shrink-0 border-r border-gray-200 flex flex-col bg-gray-50">
        <div class="px-4 pt-4 pb-2">
          <p class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">{{ $t('fleet_management.brands_title') }}</p>
          <!-- Add brand inline form -->
          <div class="flex gap-1">
            <input
              v-model="newBrandName"
              type="text"
              :placeholder="$t('fleet_management.brand_name_placeholder')"
              class="flex-1 border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:border-primary-500"
              @keydown.enter="addBrand"
            />
            <button class="btn-blue px-2 py-1.5 text-xs" @click="addBrand" :disabled="!newBrandName.trim() || isSavingBrand">
              <BaseIcon icon="plus" style="font-size:11px" />
            </button>
          </div>
        </div>

        <!-- Brand list -->
        <div class="flex-1 overflow-y-auto px-2 pb-4">
          <div v-if="isLoading" class="flex justify-center items-center h-24">
            <BaseIcon icon="spinner" class="animate-spin text-primary-500" />
          </div>
          <div v-else-if="brands.length === 0" class="text-center text-gray-400 text-xs py-8 px-3">
            {{ $t('fleet_management.no_brands') }}
          </div>
          <div
            v-for="brand in brands"
            :key="brand._id"
            class="group flex items-center justify-between rounded px-2 py-2 cursor-pointer text-sm transition-colors"
            :class="selectedBrand && selectedBrand._id === brand._id
              ? 'bg-primary-100 text-primary-800 font-semibold'
              : 'hover:bg-gray-100 text-gray-700'"
            @click="selectBrand(brand)"
          >
            <!-- Edit inline mode -->
            <div v-if="editingBrandId === brand._id" class="flex items-center gap-1 w-full" @click.stop>
              <input
                v-model="editBrandName"
                type="text"
                class="flex-1 border border-primary-400 rounded px-2 py-0.5 text-xs focus:outline-none"
                @keydown.enter="saveBrandEdit(brand)"
                @keydown.esc="cancelBrandEdit"
                ref="editBrandInput"
              />
              <button class="text-green-600 hover:text-green-800 text-xs" @click="saveBrandEdit(brand)">
                <BaseIcon icon="check" style="font-size:11px" />
              </button>
              <button class="text-gray-400 hover:text-gray-600 text-xs" @click="cancelBrandEdit">
                <BaseIcon icon="times" style="font-size:11px" />
              </button>
            </div>
            <span v-else class="flex-1 truncate text-xs">{{ brand.brand_name }}</span>
            <div v-if="editingBrandId !== brand._id" class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" @click.stop>
              <button class="text-gray-400 hover:text-primary-600 p-0.5" @click="startBrandEdit(brand)">
                <BaseIcon icon="pen" style="font-size:10px" />
              </button>
              <button class="text-gray-400 hover:text-red-500 p-0.5" @click="deleteBrandPrompt(brand)">
                <BaseIcon icon="trash" style="font-size:10px" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT: Models panel -->
      <div class="flex-1 flex flex-col overflow-hidden">
        <!-- No brand selected -->
        <div v-if="!selectedBrand" class="flex-1 flex flex-col items-center justify-center text-gray-400">
          <BaseIcon icon="arrow-left" class="text-2xl mb-2 opacity-30" />
          <p class="text-sm">{{ $t('fleet_management.no_brand_selected') }}</p>
        </div>

        <template v-else>
          <div class="px-5 pt-4 pb-2">
            <div class="flex items-center justify-between mb-3">
              <p class="text-xs font-bold text-gray-400 uppercase tracking-wider">
                {{ $t('fleet_management.models_title', { brand: selectedBrand.brand_name }) }}
                <span class="font-normal normal-case text-gray-400 ml-1">({{ selectedBrandModels.length }})</span>
              </p>
            </div>
            <!-- Add model inline form -->
            <div class="flex gap-1">
              <input
                v-model="newModelName"
                type="text"
                :placeholder="$t('fleet_management.model_name_placeholder')"
                class="flex-1 border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:border-primary-500"
                @keydown.enter="addModel"
              />
              <button class="btn-blue px-2 py-1.5 text-xs" @click="addModel" :disabled="!newModelName.trim() || isSavingModel">
                <BaseIcon icon="plus" style="font-size:11px" />
              </button>
            </div>
          </div>

          <!-- Models list -->
          <div class="flex-1 overflow-y-auto px-5 pb-4">
            <div v-if="selectedBrandModels.length === 0" class="text-center text-gray-400 text-xs py-8">
              {{ $t('fleet_management.no_models') }}
            </div>
            <div
              v-for="model in selectedBrandModels"
              :key="model._id"
              class="group flex items-center justify-between border-b border-gray-100 py-1.5"
            >
              <!-- Edit inline mode -->
              <div v-if="editingModelId === model._id" class="flex items-center gap-1 w-full">
                <input
                  v-model="editModelName"
                  type="text"
                  class="flex-1 border border-primary-400 rounded px-2 py-0.5 text-xs focus:outline-none"
                  @keydown.enter="saveModelEdit(model)"
                  @keydown.esc="cancelModelEdit"
                />
                <button class="text-green-600 hover:text-green-800 text-xs" @click="saveModelEdit(model)">
                  <BaseIcon icon="check" style="font-size:11px" />
                </button>
                <button class="text-gray-400 hover:text-gray-600 text-xs" @click="cancelModelEdit">
                  <BaseIcon icon="times" style="font-size:11px" />
                </button>
              </div>
              <span v-else class="text-sm text-gray-700 flex-1">{{ model.model_name }}</span>
              <div v-if="editingModelId !== model._id" class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button class="text-gray-400 hover:text-primary-600 p-0.5" @click="startModelEdit(model)">
                  <BaseIcon icon="pen" style="font-size:11px" />
                </button>
                <button class="text-gray-400 hover:text-red-500 p-0.5" @click="deleteModelPrompt(model)">
                  <BaseIcon icon="trash" style="font-size:11px" />
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Footer -->
    <div class="bg-gray-50 border-t border-gray-200 px-6 py-3 flex justify-end">
      <button class="btn-blue px-4 py-2 text-sm" @click="hide">
        {{ $t('fleet_management.done') }}
      </button>
    </div>
  </modal>
</template>

<script>
export default {
  name: 'FleetBrandModal',

  data() {
    return {
      brands: [],
      isLoading: false,
      isSeeding: false,
      isSavingBrand: false,
      isSavingModel: false,
      selectedBrand: null,
      // Add forms
      newBrandName: '',
      newModelName: '',
      // Inline edit – brand
      editingBrandId: null,
      editBrandName: '',
      // Inline edit – model
      editingModelId: null,
      editModelName: '',
    };
  },

  computed: {
    selectedBrandModels() {
      if (!this.selectedBrand) return [];
      const found = this.brands.find(b => b._id === this.selectedBrand._id);
      return found ? found.models : [];
    },
  },

  methods: {
    open() {
      this.$modal.show('modal-fleet-brands');
      this.loadBrands();
    },

    hide() {
      this.$modal.hide('modal-fleet-brands');
      this.$emit('closed');
    },

    async loadBrands() {
      this.isLoading = true;
      try {
        const res = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/fleet-car-brands`);
        this.brands = res.data.brands || [];
        // Re-sync selected brand after reload
        if (this.selectedBrand) {
          this.selectedBrand = this.brands.find(b => b._id === this.selectedBrand._id) || null;
        }
      } catch (err) {
        this.handleError(err);
      } finally {
        this.isLoading = false;
      }
    },

    selectBrand(brand) {
      if (this.editingBrandId) this.cancelBrandEdit();
      if (this.editingModelId) this.cancelModelEdit();
      this.newModelName = '';
      this.selectedBrand = brand;
    },

    // ── Brands ──────────────────────────────────────────
    async addBrand() {
      const name = this.newBrandName.trim();
      if (!name) return;
      this.isSavingBrand = true;
      try {
        const res = await this.axios.post(`${process.env.VUE_APP_SERVER_URL}/fleet-car-brands`, { brand_name: name });
        this.newBrandName = '';
        await this.loadBrands();
        this.selectedBrand = this.brands.find(b => b._id === res.data.brand._id) || null;
        this.handleSuccess(this.$t('fleet_management.brand_created'));
      } catch (err) {
        this.handleError(err);
      } finally {
        this.isSavingBrand = false;
      }
    },

    startBrandEdit(brand) {
      this.editingBrandId = brand._id;
      this.editBrandName = brand.brand_name;
      this.$nextTick(() => {
        const el = this.$refs.editBrandInput;
        if (el) (Array.isArray(el) ? el[0] : el).focus();
      });
    },

    cancelBrandEdit() {
      this.editingBrandId = null;
      this.editBrandName = '';
    },

    async saveBrandEdit(brand) {
      const name = this.editBrandName.trim();
      if (!name) return;
      try {
        await this.axios.put(`${process.env.VUE_APP_SERVER_URL}/fleet-car-brands/${brand._id}`, { brand_name: name });
        this.cancelBrandEdit();
        await this.loadBrands();
        this.handleSuccess(this.$t('fleet_management.brand_updated'));
      } catch (err) {
        this.handleError(err);
      }
    },

    deleteBrandPrompt(brand) {
      this.$modal.show('dialog', {
        title: `<span class="font-normal text-sm">${this.$t('fleet_management.delete_brand_confirm', { name: brand.brand_name })}</span>`,
        buttons: [
          {
            title: `<div class="bg-accent-500 text-white text-sm font-sans py-2">${this.$t('asset_tracking_table.cancel')}</div>`,
            handler: () => this.$modal.hide('dialog'),
          },
          {
            title: `<div class="bg-red-500 text-white text-sm font-sans py-2">${this.$t('asset_tracking_table.delete')}</div>`,
            handler: () => this.deleteBrand(brand),
          },
        ],
      });
    },

    async deleteBrand(brand) {
      try {
        await this.axios.delete(`${process.env.VUE_APP_SERVER_URL}/fleet-car-brands/${brand._id}`);
        if (this.selectedBrand && this.selectedBrand._id === brand._id) this.selectedBrand = null;
        this.$modal.hide('dialog');
        await this.loadBrands();
        this.handleSuccess(this.$t('fleet_management.brand_deleted'));
      } catch (err) {
        this.$modal.hide('dialog');
        this.handleError(err);
      }
    },

    // ── Models ──────────────────────────────────────────
    async addModel() {
      if (!this.selectedBrand) return;
      const name = this.newModelName.trim();
      if (!name) return;
      this.isSavingModel = true;
      try {
        await this.axios.post(`${process.env.VUE_APP_SERVER_URL}/fleet-car-brands/${this.selectedBrand._id}/models`, { model_name: name });
        this.newModelName = '';
        await this.loadBrands();
        this.handleSuccess(this.$t('fleet_management.model_created'));
      } catch (err) {
        this.handleError(err);
      } finally {
        this.isSavingModel = false;
      }
    },

    startModelEdit(model) {
      this.editingModelId = model._id;
      this.editModelName = model.model_name;
    },

    cancelModelEdit() {
      this.editingModelId = null;
      this.editModelName = '';
    },

    async saveModelEdit(model) {
      const name = this.editModelName.trim();
      if (!name) return;
      try {
        await this.axios.put(
          `${process.env.VUE_APP_SERVER_URL}/fleet-car-brands/${this.selectedBrand._id}/models/${model._id}`,
          { model_name: name }
        );
        this.cancelModelEdit();
        await this.loadBrands();
        this.handleSuccess(this.$t('fleet_management.model_updated'));
      } catch (err) {
        this.handleError(err);
      }
    },

    deleteModelPrompt(model) {
      this.$modal.show('dialog', {
        title: `<span class="font-normal text-sm">${this.$t('fleet_management.delete_model_confirm', { name: model.model_name })}</span>`,
        buttons: [
          {
            title: `<div class="bg-accent-500 text-white text-sm font-sans py-2">${this.$t('asset_tracking_table.cancel')}</div>`,
            handler: () => this.$modal.hide('dialog'),
          },
          {
            title: `<div class="bg-red-500 text-white text-sm font-sans py-2">${this.$t('asset_tracking_table.delete')}</div>`,
            handler: () => this.deleteModel(model),
          },
        ],
      });
    },

    async deleteModel(model) {
      try {
        await this.axios.delete(
          `${process.env.VUE_APP_SERVER_URL}/fleet-car-brands/${this.selectedBrand._id}/models/${model._id}`
        );
        this.$modal.hide('dialog');
        await this.loadBrands();
        this.handleSuccess(this.$t('fleet_management.model_deleted'));
      } catch (err) {
        this.$modal.hide('dialog');
        this.handleError(err);
      }
    },

    // ── Seed ─────────────────────────────────────────────
    async seedDefaults() {
      const existing = this.brands.length;
      if (existing > 0) {
        const confirmed = window.confirm(this.$t('fleet_management.seed_overwrite_confirm'));
        if (!confirmed) return;
      }
      this.isSeeding = true;
      try {
        const res = await this.axios.post(`${process.env.VUE_APP_SERVER_URL}/fleet-car-brands/seed-defaults`, { force: existing > 0 });
        await this.loadBrands();
        this.handleSuccess(this.$t('fleet_management.seed_success', { count: res.data.seeded || existing }));
      } catch (err) {
        this.handleError(err);
      } finally {
        this.isSeeding = false;
      }
    },
  },
};
</script>
